<template>
  <section id="section-slip" class="notice-section slip-section">
    <header class="notice-section__top slip-section__header">
      <div class="notice-section__heading slip-section__heading">
        <h2 class="slip-section__title">
          <span class="slip-section__title-char">{{ titleLeading }}</span><span class="slip-section__title-char slip-section__title-char--last">{{ titleTrailing }}</span><span
            v-if="unreadCount > 0"
            class="slip-unread-chip"
            :aria-label="labels.unreadAria(unreadCount)"
          >{{ labels.unreadChip(unreadCount) }}</span>
        </h2>
        <p class="slip-section__desc">{{ labels.desc }}</p>
      </div>
      <button
        type="button"
        class="slip-section__reload"
        :disabled="loading"
        :aria-label="labels.reloadAria"
        @click="reload"
      >
        <svg
          class="slip-section__reload-icon"
          :class="{ 'slip-section__reload-icon--spin': loading }"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <path
            d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5M4 12a8 8 0 1 1 2.34 5.66M4 20v-5h5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {{ labels.reload }}
      </button>
    </header>

    <nav class="notice-tabs slip-tabs" :aria-label="labels.filterAria">
      <button
        v-for="tab in filterTabs"
        :key="tab.key"
        type="button"
        class="notice-tabs__item"
        :class="{ 'notice-tabs__item--active': activeFilter === tab.key }"
        @click="setFilter(tab.key)"
      >
        {{ tab.label }}
      </button>
    </nav>

    <div class="notice-panel">
      <p v-if="loading" class="notice-section__status">{{ labels.loading }}</p>
      <p v-else-if="loadError" class="notice-section__status notice-section__status--error">{{ loadError }}</p>
      <p v-else-if="!slips.length" class="notice-section__status">{{ emptyMessage }}</p>

      <div v-else class="notice-table-wrap">
        <table class="notice-table slip-table">
          <thead>
            <tr>
              <th scope="col" class="slip-table__col-no">{{ labels.colNo }}</th>
              <th scope="col" class="slip-table__col-status">{{ labels.colStatus }}</th>
              <th scope="col" class="slip-table__col-title">{{ labels.colTitle }}</th>
              <th scope="col" class="slip-table__col-date">{{ labels.colDate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in slips"
              :key="item.idx"
              class="notice-table__row"
              :class="{ 'slip-table__row--unread': isSlipUnread(item.status) }"
            >
              <td class="slip-table__col-no">{{ rowNumber(index) }}</td>
              <td class="slip-table__col-status">
                <span class="slip-table__status" :class="slipStatusClass(item.status)">
                  {{ slipStatusLabel(item.status) }}
                </span>
              </td>
              <td class="slip-table__col-title">
                <button
                  type="button"
                  class="notice-table__title"
                  @click="openDetail(item.idx)"
                >
                  {{ item.title || labels.noTitle }}
                </button>
              </td>
              <td class="slip-table__col-date">{{ formatSlipDate(item.regdate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <footer v-if="total > 0" class="betting-pagination slip-pagination">
      <button
        type="button"
        class="betting-pagination__btn"
        :disabled="page <= 1 || loading"
        @click="goPage(page - 1)"
      >
        {{ labels.prevPage }}
      </button>
      <span class="betting-pagination__info">{{ page }} / {{ totalPages }}</span>
      <button
        type="button"
        class="betting-pagination__btn"
        :disabled="page >= totalPages || loading"
        @click="goPage(page + 1)"
      >
        {{ labels.nextPage }}
      </button>
    </footer>
  </section>
</template>

<script>
import { SystemService } from '@/services'
import { formatUtcDateTime } from '@/utils/format'
import {
  getSlipLabels,
  getSlipFilterTabs,
  SLIP_LIST_PAGE_SIZE,
  normalizeSlipList,
  resolveSlipTotal,
  slipEmptyMessage,
  isSlipUnread,
  slipStatusLabel,
  slipStatusClass
} from '@/utils/slip'

export default {
  name: 'BlackGoldSlipPage',
  props: {
    unreadCount: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      activeFilter: 'all',
      page: 1,
      pageSize: SLIP_LIST_PAGE_SIZE,
      total: 0,
      loading: false,
      loadError: '',
      slips: []
    }
  },
  computed: {
    labels () {
      const locale = this.$i18n.locale
      void locale
      return getSlipLabels()
    },
    filterTabs () {
      const locale = this.$i18n.locale
      void locale
      return getSlipFilterTabs()
    },
    activeTab () {
      return this.filterTabs.find(tab => tab.key === this.activeFilter) || this.filterTabs[0]
    },
    filterStatus () {
      return this.activeTab.status
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
    emptyMessage () {
      return slipEmptyMessage(this.activeFilter)
    },
    titleLeading () {
      const title = this.labels.title || ''
      return title.length > 1 ? title.slice(0, -1) : title
    },
    titleTrailing () {
      const title = this.labels.title || ''
      return title.length > 1 ? title.slice(-1) : ''
    }
  },
  mounted () {
    this.loadSlips()
  },
  methods: {
    formatSlipDate (value) {
      return formatUtcDateTime(value, { withSeconds: false })
    },
    isSlipUnread,
    slipStatusLabel,
    slipStatusClass,
    rowNumber (index) {
      return (this.page - 1) * this.pageSize + index + 1
    },
    setFilter (key) {
      if (this.activeFilter === key) return
      this.activeFilter = key
      this.page = 1
      this.loadSlips()
    },
    goPage (nextPage) {
      if (nextPage < 1 || nextPage > this.totalPages || nextPage === this.page) return
      this.page = nextPage
      this.loadSlips()
    },
    reload () {
      this.loadSlips()
    },
    openDetail (idx) {
      this.$emit('open-detail', idx)
    },
    async loadSlips () {
      this.loading = true
      this.loadError = ''
      try {
        const offset = (this.page - 1) * this.pageSize
        const res = await SystemService.getSlipList(offset, this.pageSize, this.filterStatus)
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data) {
          this.slips = normalizeSlipList(body.data)
          this.total = resolveSlipTotal(body.data)
          const pages = Math.max(1, Math.ceil(this.total / this.pageSize))
          if (this.page > pages) {
            this.page = pages
            if (this.page > 1 || offset > 0) {
              this.loading = false
              return this.loadSlips()
            }
          }
        } else {
          this.slips = []
          this.total = 0
          this.loadError = this.labels.loadError
        }
      } catch (err) {
        this.slips = []
        this.total = 0
        this.loadError = this.labels.loadError
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
